import {Dossier} from "@/models/dossier";
import {User} from "@/models/user";
import {Tier} from "@/utils/tier";
import { CompanyDidNotPurchaseReasons } from "./company-did-not-purchase-reason";
import {CompanyModule} from "./company-module";
import {DiscoveredUsFrom} from "./discovered-us-from";
import { PaymentFrequency } from "./plan";

export class Company {
	id: number = 0;
	name: string = "";
	tier: Tier = Tier.Starter;
	ownerId: number = 0;
	owner: User | undefined;
	activeUntil: Date = new Date();
	isDemo: boolean = false;
	Blocked: boolean = false;
	users: User[] = [];
	modules: CompanyModule[] = [];
	dossiers: Dossier[] = [];
	workspaceUrl: string = "";
	WfOfflineID: string = ""
	dateCreated: Date = new Date();
	employeeAccount: boolean = false;
	servicePackExpirationDate: Date | null = null;
	accountCreatedByWinfaktOnline: boolean = false;
	accountCreatedByWinfaktClassic: boolean = false;
	accountCreatedByFacturatieBe: boolean = false;
	discoveredUsFrom: DiscoveredUsFrom | null = null;
	hasWrittenReview: boolean = false;
	askedForReview: boolean = false;
	HasBoughtSPAtLeastOnce: boolean = false;
	paymentFrequency:PaymentFrequency = PaymentFrequency.Permanent;
	DidNotPurchaseReasons:CompanyDidNotPurchaseReasons[] = [];
	MollieCustomerID:string = "";
	dateFirstActivation:Date | null = null;
	DbID:number = 0;

	FixedRenewalPrice: number = 0;

	// eslint-disable-next-line max-lines-per-function
	constructor(data?: any) {
		if (!data) return;
		this.id = data.ID;
		this.name = data.Name;
		this.tier = data.Tier;
		this.ownerId = data.OwnerID;
		this.activeUntil = new Date(data.ActiveUntil * 1000);
		this.isDemo = data.IsDemo;
		this.Blocked = data.Blocked;
		this.WfOfflineID = data.WfOfflineID;
		this.dateCreated = new Date(data.DateCreated);
		this.employeeAccount = data.EmployeeAccount;
		if (data.WorkspaceURL) {
			this.workspaceUrl = data.WorkspaceURL;
		}
		if (data.Owner) {
			this.owner = new User(data.Owner);
		}
		if (data.Modules) {
			this.parseModules(data);
		}
		if (data.Dossiers) {
			this.dossiers = data.Dossiers.map((d: any) => new Dossier(d));
		}
		if (data.Users) {
			this.parseUsers(data);
		}
		if (data.ServicePackExpirationDate) {
			this.servicePackExpirationDate = new Date(data.ServicePackExpirationDate);
		}
		this.accountCreatedByWinfaktOnline = data.AccountCreatedByWinfaktOnline;
		this.accountCreatedByWinfaktClassic = data.AccountCreatedByWinfaktClassic;
		this.accountCreatedByFacturatieBe = data.AccountCreatedByFacturatieBe;
		this.hasWrittenReview = data.HasWrittenReview;
		this.askedForReview = data.AskedForReview;
		this.HasBoughtSPAtLeastOnce = data.HasBoughtSPAtLeastOnce;
		if (data.DiscoveredUsFrom) {
			this.discoveredUsFrom = new DiscoveredUsFrom(data.DiscoveredUsFrom);
		}
		this.paymentFrequency = data.PaymentFrequency;
		if (data.DidNotPurchaseReasons) {
			this.DidNotPurchaseReasons = data.DidNotPurchaseReasons.map((d:any)=>new CompanyDidNotPurchaseReasons(d));
		}
		if (data.DateFirstActivation) {
			this.dateFirstActivation = new Date(data.DateFirstActivation);
		}
		this.MollieCustomerID = data.MollieCustomerID;
		this.FixedRenewalPrice = data.FixedRenewalPrice;
		this.DbID = data.DbID;
	}

	private parseModules(data: any) {
		for (let m of data.Modules) {
			let data = {} as any;
			data.ID = m.ModuleID;
			data.MetaData = m.MetaData;
			if (m.Module) {
				data.Name = m.Module.Name;
			}
			this.modules.push(new CompanyModule(data));
		}
	}

	private parseUsers(data: any) {
		this.users = data.Users.map((u: any) => new User(u));
		for (let user of this.users) {
			if (user.id == this.ownerId) {
				this.owner = user;
				user.isOwner = true;
			}
		}
	}

	get tierAsString(): string {
		let tier = "Starter";
		if (this.tier == Tier.Pro) {
			tier = "Professional";
		} else if (this.tier == Tier.ProfessionalPlus) {
			tier = "Prof PLUS";
		} else if (this.tier == Tier.Enterprise) {
			tier = "Enterprise";
		} else if (this.tier == Tier.Free) {
			tier = "Free";
		}

		if (this.isDemo) {
			return `Demo`;
		}
		return tier;
	}

	static getActiveUntilString(value:Date){
		if (value.getTime() == 0) {
			return "Permanent";
		}
		const now = new Date();
		if (value.getTime() <= now.getTime()) {
			const diffMs = now.getTime() - value.getTime();
			const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
			if (diffDays < 31) {
				return `Verlopen (${diffDays}d.)`;
			}
			const diffMonths = (now.getFullYear() - value.getFullYear()) * 12 + (now.getMonth() - value.getMonth());
			if (diffMonths < 12) {
				return `Verlopen (${diffMonths}m.)`;
			}
			return "Verlopen (+1j.)";
		}
		return value.toLocaleDateString("nl-be");
	}

	get activeUntilString(): string {
		return Company.getActiveUntilString(this.activeUntil);
	}

	get serviceUntilString(): string {
		if (!this.servicePackExpirationDate) {
			return "Permanent";
		}
		if (this.servicePackExpirationDate.getTime() == 0) {
			return "-";
		}
		if (this.servicePackExpirationDate.getTime() <= new Date().getTime()) {
			return "<span class='expired' >" + this.servicePackExpirationDate.toLocaleDateString("nl-be") + "</span>";
		}
		return this.servicePackExpirationDate.toLocaleDateString("nl-be");
	}

	get servicePackString(): string {
		if (this.tier == Tier.Free) {
			return "Geen servicepakket (Free)";
		}
		if (!this.servicePackExpirationDate) {
			//permanent
			if(this.activeUntil.getTime() == 0){
				return "permanent";
			}
			if (this.activeUntil.getTime() <= new Date().getTime()) {
				return "Verlopen op " + this.activeUntil.toLocaleDateString("nl-be") ;
			}
			return this.activeUntil.toLocaleDateString("nl-be");

		}
		if (this.servicePackExpirationDate.getTime() <= new Date().getTime()) {
			return "Verlopen op " + this.servicePackExpirationDate.toLocaleDateString("nl-be") ;
		}
		return this.servicePackExpirationDate.toLocaleDateString("nl-be");
	}


	get modulesAsString(): string {
		if (this.modules.length == 0) {
			return "None";
		}
		return this.modules.map((m) => m.name).join(", ");
	}

	get dossiersAsString(): string {
		if (this.dossiers.length == 0) {
			return "None";
		}
		return this.dossiers.map((d) => d.name).join(", ");
	}

	get isExpired():boolean {
		if (this.activeUntil.getTime()==0) return false;
		return this.activeUntil.getTime() < new Date().getTime();
	}

	get isServicePackExpired():boolean {
		if (this.tier == Tier.Free) return true;
		if (!this.servicePackExpirationDate) return false;
		return this.servicePackExpirationDate.getTime() < new Date().getTime();
	}

	getJSON() {
		return {
			ID: this.id,
			Name: this.name,
			Tier: this.tier,
			OwnerID: this.ownerId,
			ActiveUntil: Math.round(this.activeUntil.getTime() / 1000),
			IsDemo: this.isDemo,
			Blocked: this.Blocked,
			WorkspaceURL: this.workspaceUrl || null,
			WfOfflineID: this.WfOfflineID,
			DateCreated: this.dateCreated.toJSON(),
			EmployeeAccount: this.employeeAccount,
			ServicePackExpirationDate: this.servicePackExpirationDate,
			AccountCreatedByWinfaktOnline: this.accountCreatedByWinfaktOnline,
			AccountCreatedByWinfaktClassic: this.accountCreatedByWinfaktClassic,
			AccountCreatedByFacturatieBe: this.accountCreatedByFacturatieBe,
			HasWrittenReview: this.hasWrittenReview,
			AskedForReview: this.askedForReview,
			HasBoughtSPAtLeastOnce: this.HasBoughtSPAtLeastOnce,
			PaymentFrequency: this.paymentFrequency,
			DidNotPurchaseReasons: this.DidNotPurchaseReasons.map(d=>d.getJSON()),
			MollieCustomerID: this.MollieCustomerID,
			DateFirstActivation: this.dateFirstActivation,
			FixedRenewalPrice: this.FixedRenewalPrice
		};
	}

	public getBrowseClass(): string[] {
		if (this.isDemo) {
			return ["demo"];
		}
		if (this.servicePackExpirationDate && this.servicePackExpirationDate.getTime() <= new Date().getTime()) {
			return ["expired"];
		}
		return [];
	}
}
